/** @jsxImportSource @emotion/react */
// The Tooltip module itself, not the Widgets barrel: the barrel leads back into the Partials that
// import this file, leaving the column constants undefined at import time.
import { ExpandCaret } from '@/Client/Icons/ExpandCaret';
import { useTooltip } from '@/Client/Widgets/Tooltip';
import type { ActivityLogTableStyles } from './activityLogTableStyles';
import type { ActivityLogTableColumn, ActivityLogTableEntry } from './activityLogTableTypes';

/** Wide enough for a two-column table of changes. */
const ROW_TOOLTIP_MAX_WIDTH = 480;

/** One entry as a table row: a cell per column, ruled below unless it ends its year's run, with the entry's tooltip on hover. */
export const ActivityLogTableRow = <Entry extends ActivityLogTableEntry>({ entry, columns, isFirst, isLast, expansion, styles }: {
  entry: Entry;
  columns: readonly ActivityLogTableColumn<Entry>[];
  isFirst: boolean;
  isLast: boolean;
  /** For a row standing for collapsed events: whether they're shown, and the caret's toggle. */
  expansion?: { expanded: boolean; onToggle: () => void };
  styles: ActivityLogTableStyles;
}) => {
  const { anchorProps, tooltip } = useTooltip(entry.tooltip, { title: entry.tooltipTitle, maxWidth: ROW_TOOLTIP_MAX_WIDTH });
  return (
    <tr
      data-testid={'activity-log-table-row'}
      {...(entry.tooltip ? { ...anchorProps, tabIndex: 0 } : {})}
      // anywhere on a collapsible row toggles it; the caret stays the keyboard's way in
      css={expansion && styles.expandableRow}
      onClick={expansion?.onToggle}
    >
      {columns.map((column, columnIndex) => (
        <td
          key={column.key}
          data-testid={`activity-log-table-cell-${column.key}`}
          css={[
            styles.cell,
            isLast && styles.lastRowCell,
            columnIndex === columns.length - 1 && styles.lastColumnCell,
            column.cellStyle?.(styles, { isFirst, isLast }),
            columnIndex === 0 && expansion && styles.expandableFirstCell,
          ]}
        >
          {columnIndex === 0 && expansion && (
            <button
              type={'button'}
              css={styles.expandCaretButton}
              data-testid={'activity-log-table-expand-caret'}
              aria-expanded={expansion.expanded}
              aria-label={`${expansion.expanded ? 'Collapse' : 'Expand'} ${entry.name}`}
              onClick={event => {
                event.stopPropagation();
                expansion.onToggle();
              }}
            >
              <ExpandCaret expanded={expansion.expanded} initialRotation={90} finalRotation={180} />
            </button>
          )}
          {column.render(entry, styles)}
        </td>
      ))}
      {tooltip}
    </tr>
  );
};
